import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Stack,
  Title,
  Group,
  Button,
  Card,
  Badge,
  Text,
  Tabs,
  Table,
  Loader,
  Paper,
  Progress,
  Grid,
  ThemeIcon,
} from '@mantine/core'
import {
  IconArrowLeft,
  IconSettings,
  IconList,
  IconFileText,
  IconChartBar,
  IconContainer,
  IconCpu,
  IconDeviceFloppy,
  IconNetwork,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { servicesApi } from '@/api'
import { ServiceLogs } from './ServiceLogs'
import type { Task } from '@/types'

interface ServiceDetailProps {
  serviceId: string
}

export function ServiceDetail({ serviceId }: ServiceDetailProps) {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<string | null>('overview')

  const { data: service, isLoading: serviceLoading } = useQuery({
    queryKey: ['services', serviceId],
    queryFn: async () => {
      const { data } = await servicesApi.get(serviceId)
      return data
    },
  })

  const { data: tasks, isLoading: tasksLoading } = useQuery({
    queryKey: ['services', serviceId, 'tasks'],
    queryFn: async () => {
      const { data } = await servicesApi.getTasks(serviceId)
      return data
    },
    enabled: !!service,
  })

  if (serviceLoading || !service) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading service details...</Text>
      </Stack>
    )
  }

  const serviceName = service.Spec?.Name || serviceId
  const replicas = service.Spec?.Mode?.Replicated?.Replicas || 0
  const runningTasks = tasks?.filter((t: Task) => t.Status?.State === 'running').length || 0
  const image = service.Spec?.TaskTemplate?.ContainerSpec?.Image || 'unknown'

  // Get status color based on running tasks vs desired replicas
  const getStatusColor = () => {
    if (runningTasks === 0) return 'red'
    if (runningTasks < replicas) return 'yellow'
    return 'green'
  }

  const getStatusText = () => {
    if (runningTasks === 0) return 'Stopped'
    if (runningTasks < replicas) return 'Degraded'
    return 'Running'
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() => navigate('/services')}
          >
            Back
          </Button>
          <Title order={2}>{serviceName}</Title>
        </Group>
        <Group>
          <Badge color={getStatusColor()} variant="dot" size="lg">
            {getStatusText()}
          </Badge>
          <Badge color="blue" variant="outline">
            {runningTasks}/{replicas} replicas
          </Badge>
        </Group>
      </Group>

      {/* Quick Stats */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Paper p="md" withBorder>
            <Group>
              <ThemeIcon size="lg" variant="light" color="blue">
                <IconContainer size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Replicas</Text>
                <Text fw={700}>{runningTasks}/{replicas}</Text>
              </div>
            </Group>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Paper p="md" withBorder>
            <Group>
              <ThemeIcon size="lg" variant="light" color="green">
                <IconCpu size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">CPU Limit</Text>
                <Text fw={700}>
                  {service.Spec?.TaskTemplate?.Resources?.Limits?.NanoCPUs
                    ? `${(service.Spec.TaskTemplate.Resources.Limits.NanoCPUs / 1e9).toFixed(2)} cores`
                    : 'No limit'}
                </Text>
              </div>
            </Group>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Paper p="md" withBorder>
            <Group>
              <ThemeIcon size="lg" variant="light" color="violet">
                <IconDeviceFloppy size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Memory Limit</Text>
                <Text fw={700}>
                  {service.Spec?.TaskTemplate?.Resources?.Limits?.MemoryBytes
                    ? `${(service.Spec.TaskTemplate.Resources.Limits.MemoryBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
                    : 'No limit'}
                </Text>
              </div>
            </Group>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Paper p="md" withBorder>
            <Group>
              <ThemeIcon size="lg" variant="light" color="orange">
                <IconNetwork size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Networks</Text>
                <Text fw={700}>
                  {service.Spec?.TaskTemplate?.Networks?.length || 0}
                </Text>
              </div>
            </Group>
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconSettings size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="tasks" leftSection={<IconList size={16} />}>
            Tasks ({tasks?.length || 0})
          </Tabs.Tab>
          <Tabs.Tab value="logs" leftSection={<IconFileText size={16} />}>
            Logs
          </Tabs.Tab>
          <Tabs.Tab value="metrics" leftSection={<IconChartBar size={16} />}>
            Metrics
          </Tabs.Tab>
        </Tabs.List>

        {/* Overview Tab */}
        <Tabs.Panel value="overview" pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card shadow="sm" p="lg" radius="md" withBorder>
                <Text size="lg" fw={600} mb="md">Configuration</Text>
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text fw={500}>Image</Text>
                    <Text c="dimmed" size="sm" style={{ wordBreak: 'break-all' }}>
                      {image}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text fw={500}>Mode</Text>
                    <Text c="dimmed">
                      {service.Spec?.Mode?.Replicated ? 'Replicated' : 'Global'}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text fw={500}>Update Parallelism</Text>
                    <Text c="dimmed">
                      {service.Spec?.UpdateConfig?.Parallelism || 1}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text fw={500}>Rollback Parallelism</Text>
                    <Text c="dimmed">
                      {service.Spec?.RollbackConfig?.Parallelism || 1}
                    </Text>
                  </Group>
                </Stack>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card shadow="sm" p="lg" radius="md" withBorder>
                <Text size="lg" fw={600} mb="md">Timestamps</Text>
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text fw={500}>Created</Text>
                    <Text c="dimmed">
                      {service.CreatedAt ? new Date(service.CreatedAt).toLocaleString() : 'N/A'}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text fw={500}>Updated</Text>
                    <Text c="dimmed">
                      {service.UpdatedAt ? new Date(service.UpdatedAt).toLocaleString() : 'N/A'}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text fw={500}>Version</Text>
                    <Text c="dimmed">
                      {service.Version?.Index || 'N/A'}
                    </Text>
                  </Group>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>

          {/* Environment Variables */}
          {service.Spec?.TaskTemplate?.ContainerSpec?.Env &&
           service.Spec.TaskTemplate.ContainerSpec.Env.length > 0 && (
            <Card shadow="sm" p="lg" radius="md" withBorder mt="md">
              <Text size="lg" fw={600} mb="md">Environment Variables</Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Value</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {service.Spec.TaskTemplate.ContainerSpec.Env.map((env: string, idx: number) => {
                    const [name, ...valueParts] = env.split('=')
                    const value = valueParts.join('=')
                    return (
                      <Table.Tr key={idx}>
                        <Table.Td><Text fw={500}>{name}</Text></Table.Td>
                        <Table.Td>
                          <Text c="dimmed" style={{ wordBreak: 'break-all' }}>
                            {value.includes('secret') || value.includes('password') || value.includes('key')
                              ? '••••••••'
                              : value}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            </Card>
          )}
        </Tabs.Panel>

        {/* Tasks Tab */}
        <Tabs.Panel value="tasks" pt="md">
          {tasksLoading ? (
            <Stack align="center" p="xl">
              <Loader size="sm" />
              <Text c="dimmed">Loading tasks...</Text>
            </Stack>
          ) : !tasks || tasks.length === 0 ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">No tasks found for this service</Text>
            </Paper>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Task ID</Table.Th>
                  <Table.Th>Node</Table.Th>
                  <Table.Th>State</Table.Th>
                  <Table.Th>Desired State</Table.Th>
                  <Table.Th>Created</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {tasks.map((task: Task) => (
                  <Table.Tr key={task.ID}>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {task.ID?.slice(0, 12)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {task.NodeID?.slice(0, 12) || 'Unassigned'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={
                          task.Status?.State === 'running' ? 'green' :
                          task.Status?.State === 'pending' ? 'yellow' :
                          task.Status?.State === 'failed' ? 'red' :
                          'gray'
                        }
                        size="sm"
                      >
                        {task.Status?.State || 'unknown'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="outline" size="sm">
                        {task.DesiredState || 'running'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {task.CreatedAt
                          ? new Date(task.CreatedAt).toLocaleString()
                          : 'N/A'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Tabs.Panel>

        {/* Logs Tab */}
        <Tabs.Panel value="logs" pt="md">
          <ServiceLogs serviceId={serviceId} serviceName={serviceName} />
        </Tabs.Panel>

        {/* Metrics Tab */}
        <Tabs.Panel value="metrics" pt="md">
          <Paper p="xl" withBorder ta="center">
            <Text c="dimmed">
              Metrics visualization coming soon. Use the Prometheus/Grafana integration for detailed metrics.
            </Text>
          </Paper>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  )
}
